import { createPortal } from "react-dom";
import { ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { IRegistryInvoice } from "dto/Invoices/IRegistryInvoice";
import { IAttachment } from "dto/IAttachment";
import { ItemKind } from "dto/IKindItems";
import PDFViewer from "components/PDFViewer";
import PDFContext, { DEFAULT_PDF_CONTEXT, IPDF } from "contexts/PDFContext";
import CourtPortalService from "api/CourtPortalService";
import usePDF from "hooks/usePDF";
import KeysContext from "contexts/KeysContext";
import CloseIcon from "assets/icons/close.svg?react";
import styles from "./bookingSlide.module.scss";

// ── Klucze localStorage ───────────────────────────────────────────────────────

const LS_SPLIT_KEY = "booking_slide_split_v1";
const LS_ZOOM_KEY  = "booking_slide_zoom_v1";

const DEFAULT_SPLIT = 0.55;
const MIN_SPLIT     = 0.20;
const MAX_SPLIT     = 0.82;
const FETCH_OWNER_KIND = ItemKind.KSeFInvoice;

const readFloat = (key: string, fallback: number, min?: number, max?: number): number => {
  try {
    const v = parseFloat(localStorage.getItem(key) ?? "");
    if (!isNaN(v) && (min === undefined || v >= min) && (max === undefined || v <= max)) return v;
  } catch { /* ignore */ }
  return fallback;
};

const writeFloat = (key: string, value: number) => {
  try { localStorage.setItem(key, String(value)); } catch { /* ignore */ }
};

// ── Interfejs ─────────────────────────────────────────────────────────────────

interface Props {
  invoice: IRegistryInvoice;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

// ── Komponent ─────────────────────────────────────────────────────────────────

const InvoicePdfSlide = ({ invoice, title, onClose, children }: Props) => {
  const isKsef = invoice.SourceType === "K" || !!invoice.KsefNumber;

  // ── Stan PDF ────────────────────────────────────────────────────────────────

  const [pdfBlob, setPdfBlob]       = useState<Blob | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError]     = useState<string | null>(null);

  const { decryptPackage } = usePDF();
  const { keys, setKeys } = useContext(KeysContext);
  const { mutateAsync: getKsefPreview } = CourtPortalService.usePreviewPdf();

  // ── Izolowany kontekst PDF (zoom nie przecieka do globalnego PDFContext) ─────

  const [localPdf, setLocalPdf] = useState<IPDF>(() => ({
    ...DEFAULT_PDF_CONTEXT,
    zoom: readFloat(LS_ZOOM_KEY, DEFAULT_PDF_CONTEXT.zoom, 0.1, 8),
  }));

  const setLocalPdfAndSave = useCallback((newPdf: IPDF) => {
    setLocalPdf(newPdf);
    writeFloat(LS_ZOOM_KEY, newPdf.zoom);
  }, []);

  // ── Proporcje podziału ekranu ─────────────────────────────────────────────

  const [split, setSplit] = useState<number>(() =>
    readFloat(LS_SPLIT_KEY, DEFAULT_SPLIT, MIN_SPLIT, MAX_SPLIT),
  );
  const splitRef     = useRef(split);
  splitRef.current   = split;
  const containerRef = useRef<HTMLDivElement>(null);

  const isDragging   = useRef(false);
  const dragStartX   = useRef(0);
  const dragStartSpl = useRef(DEFAULT_SPLIT);
  const [dragging, setDragging] = useState(false);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    const totalW = containerRef.current.offsetWidth;
    if (totalW <= 0) return;
    const dx = e.clientX - dragStartX.current;
    const next = Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, dragStartSpl.current + dx / totalW));
    setSplit(next);
  }, []);

  const onMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    setDragging(false);
    writeFloat(LS_SPLIT_KEY, splitRef.current);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  const handleDividerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current   = true;
    dragStartX.current   = e.clientX;
    dragStartSpl.current = splitRef.current;
    setDragging(true);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => () => {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove, onMouseUp]);

  // ── Klucze faktury ─────────────────────────────────────────────────────────

  useEffect(() => {
    const objectKeys = invoice.ObjectKeys ?? [];
    if (objectKeys.length === 0) return;
    const missing = objectKeys.filter((k) => !keys.some((x) => x.Id === k.Id));
    if (missing.length > 0) setKeys([...keys, ...missing]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-załaduj PDF ───────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      setPdfLoading(true);
      setPdfError(null);
      try {
        if (isKsef && invoice.KsefNumber) {
          const res = await getKsefPreview({ id: invoice.KsefNumber });
          setPdfBlob(
            new Blob([(res as { data: ArrayBuffer }).data], { type: "application/pdf" }),
          );
        } else if (invoice.FileUri && invoice.EncryptionKeyId) {
          const attachment = {
            Id: invoice.Id,
            Uri: invoice.FileUri,
            FileName: invoice.FileName ?? "faktura.pdf",
            EncryptionKeyId: invoice.EncryptionKeyId,
            Kind: FETCH_OWNER_KIND,
            ContentType: "application/pdf",
          } as unknown as IAttachment;
          const blob = await decryptPackage(attachment, FETCH_OWNER_KIND, false);
          setPdfBlob(blob);
        }
      } catch {
        setPdfError("Nie udało się wczytać podglądu PDF.");
      } finally {
        setPdfLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  return createPortal(
    <div
      ref={containerRef}
      className={`${styles.overlay}${dragging ? ` ${styles.overlayDragging}` : ""}`}
    >
      {/* Lewa strona — podgląd PDF */}
      <div className={styles.pdfPanel} style={{ flexBasis: `${split * 100}%` }}>
        {pdfLoading && (
          <div className={styles.pdfState}>Ładowanie podglądu faktury…</div>
        )}
        {pdfError && (
          <div className={`${styles.pdfState} ${styles.pdfStateError}`}>{pdfError}</div>
        )}
        {!pdfLoading && !pdfBlob && !pdfError && (
          <div className={styles.pdfState}>Brak pliku PDF dla tej faktury.</div>
        )}
        {pdfBlob && (
          <PDFContext.Provider value={{ PDF: localPdf, setPDF: setLocalPdfAndSave }}>
            <PDFViewer
              file={pdfBlob}
              title="Podgląd faktury"
              showCloseButton={false}
              showSignButton={false}
            />
          </PDFContext.Provider>
        )}
      </div>

      {/* Uchwyt zmiany proporcji */}
      <div
        className={`${styles.divider}${dragging ? ` ${styles.dividerActive}` : ""}`}
        onMouseDown={handleDividerMouseDown}
        title="Przeciągnij, aby zmienić proporcje"
      >
        <div className={styles.dividerHandle} />
      </div>

      {/* Prawa strona — treść akcji */}
      <div className={styles.formPanel}>
        <div className={styles.formHeader}>
          <span>{title}</span>
          <CloseIcon className={styles.closeIcon} onClick={onClose} />
        </div>
        <div className={styles.formContent}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default InvoicePdfSlide;
